import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  Chip,
  IconButton,
  Snackbar,
  Alert,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import { useAuth } from '../../contexts/AuthContext';
import { useDealerAdmin } from '../../contexts/DealerAdminContext';
import { getSites } from '../../api/backendApi';
import SiteEditorDialog from '../../components/admin/sites/SiteEditorDialog';
import MainLayout from '../../components/layout/MainLayout';

const SitesListPage = ({ embedded = false, tenantId: tenantIdProp } = {}) => {
  const { currentTenant, userData, role } = useAuth();
  const dealerAdmin = useDealerAdmin();
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [selectedSite, setSelectedSite] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const tenantId = tenantIdProp || (dealerAdmin.active ? dealerAdmin.tenantId : '') || userData?.tenantId || currentTenant?.id;
  const userRole = role || userData?.role;
  const canManageSites =
    Boolean(dealerAdmin.active) ||
    userRole === 'tenant_admin' || userRole === 'admin' || userRole === 'superadmin';

  const loadSites = useCallback(async () => {
    if (!tenantId) {
      setSites([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const response = await getSites(tenantId);
      const rows = Array.isArray(response?.data) ? response.data : [];
      setSites(rows);
    } catch (error) {
      setSites([]);
      setSnackbar({
        open: true,
        message: `Error loading sites: ${error.message || 'Request failed'}`,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    loadSites();
  }, [loadSites]);

  const handleAddSite = () => {
    setSelectedSite(null);
    setEditorOpen(true);
  };

  const handleEditSite = (site) => {
    setSelectedSite(site);
    setEditorOpen(true);
  };

  const handleEditorClose = () => {
    setEditorOpen(false);
    setSelectedSite(null);
  };

  const handleEditorSuccess = (message) => {
    setSnackbar({ open: true, message, severity: 'success' });
    setEditorOpen(false);
    setSelectedSite(null);
    loadSites();
  };

  const handleEditorError = (message) => {
    setSnackbar({ open: true, message, severity: 'error' });
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const columns = [
    {
      field: 'name',
      headerName: 'Site Name',
      flex: 1,
      minWidth: 200,
      sortable: true,
    },
    {
      field: 'city',
      headerName: 'City',
      flex: 0.8,
      minWidth: 120,
      sortable: true,
      valueGetter: (value, row) => row.address?.city || '-',
    },
    {
      field: 'state',
      headerName: 'Province/State',
      flex: 0.5,
      minWidth: 80,
      sortable: true,
      valueGetter: (value, row) => row.address?.state || '-',
    },
    {
      field: 'timezone',
      headerName: 'Timezone',
      flex: 1,
      minWidth: 180,
      sortable: true,
      valueGetter: (value, row) => row.settings?.timezone || '-',
    },
    {
      field: 'isDefault',
      headerName: 'Default',
      flex: 0.5,
      minWidth: 100,
      sortable: true,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) =>
        params.row.isDefault ? (
          <Chip label="Default" color="primary" size="small" />
        ) : null,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.5,
      minWidth: 100,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <IconButton
          size="small"
          onClick={() => handleEditSite(params.row)}
          disabled={!canManageSites}
          color="primary"
        >
          <EditIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  const content = (
      <Box sx={{ p: embedded ? 0 : 3 }} data-testid="sites-list-page">
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: embedded ? 2 : 3 }}>
          <Typography variant={embedded ? 'h6' : 'h4'} component={embedded ? 'h3' : 'h1'}>
            Sites
          </Typography>
          {canManageSites && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleAddSite}
            >
              Add Site
            </Button>
          )}
        </Box>

      <Paper sx={{ height: embedded ? 420 : 600, width: '100%' }}>
        <DataGrid
          rows={sites}
          columns={columns}
          loading={loading}
          pageSize={10}
          rowsPerPageOptions={[10, 25, 50]}
          disableSelectionOnClick
          sx={{
            '& .MuiDataGrid-cell:focus': {
              outline: 'none',
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        />
      </Paper>

      {editorOpen && (
        <SiteEditorDialog
          open={editorOpen}
          site={selectedSite}
          tenantId={tenantId}
          onClose={handleEditorClose}
          onSuccess={handleEditorSuccess}
          onError={handleEditorError}
        />
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      </Box>
  );

  return embedded ? content : <MainLayout>{content}</MainLayout>;
};

export default SitesListPage;
